import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useHydrated } from 'remix-utils';
import { useFixedUIHidden } from '~/modules/root';
import { useSiteFeedbackEnabled } from '~/modules/site-feedback/util/is-site-feedback-audience';
import { EditModeToggle } from './EditModeToggle';
import { CopyLinkButton } from './CopyLinkButton';
import {
    VisibilityToggleButton,
    type VisibilityItemType,
} from './VisibilityToggleButton';
import { PinToDirectoryButton } from './PinToDirectoryButton';
import { StaffAvatarButton } from './StaffAvatarButton';
import { FeedbackIslandButton } from './FeedbackIslandButton';
import {
    DownloadImagesButton,
    type DownloadImagesConfig,
} from './DownloadImagesButton';
import { StaffIslandFrame, useRegisterStaffIsland } from './StaffIslandFrame';
import makeSharableInternalLink from '../../util/shareable-internal-link';

interface DetailPageButtonsProps {
    /** The ID of the directory item. */
    id: number;
    /** The link to the edit page (Business Manager). */
    editLink: string;
    /** The shareable link (with tracking query params added). */
    shareableLink: string;
    /** The current user has permission to edit this item. */
    canEdit: boolean;
    /** The current user has permission to copy the shareable link. */
    canCopyLink: boolean;
    /** Whether to show the buttons. Defaults to `true`. */
    showButton?: boolean;
    /** When provided, renders a visibility toggle button for admin users. */
    visibilityToggle?: {
        isInDirectory: boolean;
        itemType: VisibilityItemType;
        onToggle: (inLibrary: boolean) => void;
        disabled?: boolean;
        /** Extra line appended to the remove-from-directory confirm copy. */
        removalNote?: string;
    };
    /** When provided, renders a pin-to-directory toggle button for admin users. */
    pinToggle?: {
        isPinned: boolean;
        isInDirectory: boolean;
        onToggle: (pinned: boolean) => void;
    };
    /**
     * The page's own photographs, for staff to take away as originals. Passed
     * by the product, project and article pages only.
     */
    downloadImages?: DownloadImagesConfig;
}

/**
 * The staff island on directory item detail pages (professional, product,
 * project, article): the reader's tools on this item, each permission-gated,
 * and the feedback control at the end of the row for staff on the revamp.
 */
export const DetailPageButtons = ({
    id,
    editLink,
    shareableLink,
    canEdit,
    canCopyLink,
    showButton = true,
    visibilityToggle,
    pinToggle,
    downloadImages,
}: DetailPageButtonsProps) => {
    const isHydrated = useHydrated();
    const isFixedUIHidden = useFixedUIHidden();
    const feedbackEnabled = useSiteFeedbackEnabled();
    const { desktop } = useAppDisplayModeContext();

    // Every action is permission-gated, but the frame around them is not, so
    // this has to be checked here: a reader with none of them would otherwise
    // get the frosted pill on its own — a small empty circle on a public detail
    // page, and `pointer-events-auto` would have it swallow clicks. Before the
    // frame the empty container had no size and no surface, so it cost nothing.
    // The avatar does not count: it says who is holding the tools, and with no
    // tools there is nothing for it to introduce.
    // The feedback control counts: with it, a staff reader always has
    // something here, and the shell's feedback-only island stands down.
    const hasActions =
        canEdit ||
        canCopyLink ||
        !!pinToggle ||
        !!visibilityToggle ||
        feedbackEnabled;
    const shown = desktop && hasActions;
    useRegisterStaffIsland(shown);
    if (!shown) return null;

    const internalLink = isHydrated
        ? makeSharableInternalLink(window.location.origin, shareableLink, id)
        : '';

    return (
        <StaffIslandFrame hidden={!showButton || isFixedUIHidden}>
            <StaffAvatarButton />
            {pinToggle && <PinToDirectoryButton {...pinToggle} />}
            {visibilityToggle && (
                <VisibilityToggleButton {...visibilityToggle} />
            )}
            {canCopyLink && <CopyLinkButton url={internalLink} />}
            {canEdit && <EditModeToggle editLink={editLink} />}
            {downloadImages && <DownloadImagesButton {...downloadImages} />}
            {feedbackEnabled && <FeedbackIslandButton />}
        </StaffIslandFrame>
    );
};
